import {httpHandler} from '../../../util/alme-api';
import {HttpRequestMethods} from '@alme/api-ts/HttpHandler';
import { isActiveSession } from '../../../util/session';
import {settings} from '../../../settings/settings-util';
import {processResponse} from './actions/actions';
import reducer from './reducers/index';
import {Reducer, Store} from 'redux';
import {AlmeState} from '../../../wireup/AlmeState';
import InputProcessor from '../../../types/input-processor';
import ResponseHandler from '../../../types/response-handler';
import {AlmeUIInput} from '../../../types/input';
import {AlmeUIMessage} from '../../../types/response';
import reduceReducers from 'reduce-reducers';
import * as requests from './request-mapper';
import {AlmeResponseV5} from "@alme/types/response/AlmeResponse";

let _inputRequestProcessors: InputProcessor[];
let _responseHandlers: ResponseHandler[];
let _store: Store<AlmeState> | null = null;
let _settings: typeof settings;
const shouldLog = process.env.converse_console_logging_enabled;

const validRequestTypes = [
    settings.messageConstants.converseRequest,
    settings.messageConstants.requestUnitRequest,
    settings.messageConstants.appeventRequest,
    settings.messageConstants.reportingEventRequest,
    settings.messageConstants.getHistoryRequest,
    settings.messageConstants.getConfigurationRequest,
    settings.messageConstants.getNotificationRequest,
    settings.messageConstants.extensibleRequest,
];

function setInputRequestProcessors(inputRequestProcessors): void {
    _inputRequestProcessors = inputRequestProcessors;
}

function setResponseHandlers(responseHandlers): void {
    _responseHandlers = responseHandlers;
}

function setSettings(passedSettings: typeof settings): void{
    _settings = passedSettings ?? settings;
}

function setStore(store: Store<AlmeState>): void {
    _store = store;
    if(!_settings){
        _settings = settings;
    }
}

function getReducer(): Reducer<AlmeState> {
    const responseHandlerReducers = _responseHandlers.reduce((reducers: Reducer<AlmeState>[], handler: ResponseHandler): Reducer<AlmeState>[] => {
        if (typeof handler.getReducer === 'function') {
            reducers.push(handler.getReducer());
        }
        return reducers;
    }, []);

    const reducers = responseHandlerReducers.concat(reducer);

    return reduceReducers(...reducers);
}

function handleInputProcessing(startingInput: AlmeUIInput): AlmeUIInput {
    return _inputRequestProcessors.reduce((inputObj: AlmeUIInput, currentProcessor: InputProcessor) => {
        return currentProcessor.process.bind(currentProcessor)(inputObj);
    }, startingInput);
}

function logAndReduceResponse(pre: AlmeUIMessage, current: ResponseHandler, index: number): AlmeUIMessage {
    const preJson = JSON.stringify(pre, null, 2);
    const post = current.process(pre);
    const postJson = JSON.stringify(post, null, 2);
    if (preJson !== postJson) {
        console.log(`RESPONSE HANDLER #${index} (${current.constructor.name}.${current.process.name}):`);
        console.log(`*** response changed from: ***`);
        console.log(JSON.parse(preJson));
        console.log('*** to: ***');
        console.log(JSON.parse(postJson));
    }
    return post;
}

function reduceResponse(pre: AlmeUIMessage, current: ResponseHandler): AlmeUIMessage {
    return current.process.bind(current)(pre);
}

function generateErrorText(statusCode: number): string {
    const isAuthError = statusCode === 401;
    const isOnLaunch  = !isActiveSession(_settings, _store);
    return ((isAuthError) ? _settings.textConstants.authErrorResponse :
            (isOnLaunch ) ? _settings.textConstants.launchErrorResponse : _settings.textConstants.defaultErrorResponse
    );
}

function generateErrorResponse(): AlmeResponseV5 {
    return {
        userDisplayName: "",
        agentDisplayName: "",
        messageId: "",
        responseId: "",
        unitId: "",
        maintainUiLock: false,
        userId: "",
        responseRevision: 0,
        maskedInput: null,
        text: "",
        navUrl : null,
        displayLinkCollection: null,
        responseActions: null,
        deferredAppCall: null,
        isReverted: false,
        extensionData: {},
    };
}

async function sendExtensibleRequest(input: AlmeUIInput, method?: HttpRequestMethods): Promise<AlmeUIMessage | void> {
    let requestInit, requestInfo, callbacks, response;
    input = handleInputProcessing(input);
    input.accessKey = input.accessKey ?? _settings.MultiTenant?.DefaultAccessKey;
    try {
        requestInfo = _settings.AlmeAPI.ALME_API_PROTOCOL + _settings.AlmeAPI.ALME_API_HOSTNAME + _settings.AlmeAPI.ALME_API_BASE_PATH + input.endpoint;
        delete input.endpoint;
        callbacks = input.callbacks;
        delete input.callbacks;
        requestInit = method !== "GET" ? { method, body: JSON.stringify(input)} : { method };
        response = await httpHandler.SendAsync(
            requestInfo,
            requestInit
        );
        if (callbacks && callbacks.onSuccess && typeof callbacks.onSuccess === "function")
        {
            callbacks.onSuccess(input, response);
        }
        const result = {
            messageType: input.messageType,
            messageSource: input.messageSource,
            response };
        return await handleResponseProcessing(result);
    }
    catch (e) {
        if(callbacks && callbacks.onFailure && typeof callbacks.onFailure === "function")
        {
            callbacks.onFailure();
        }
    }

    return;
}

async function handleResponseProcessing(received: AlmeUIMessage): Promise<AlmeUIMessage> {
    if (!received) {
        return {};
    }

    const reduce = shouldLog ? logAndReduceResponse : reduceResponse;
    const processedResponse = await _responseHandlers.reduce(async (pre, current, index) => {
        return Promise.resolve(reduce(await pre, current, index));
    }, Promise.resolve(received));

    if (_store) {
        _store.dispatch(processResponse(
            _settings.stateLocations.Conversation.processedResponse,
            processedResponse.response,
            processedResponse.messageType ?? received.messageType,
            processedResponse.error, processedResponse.errorText,
            processedResponse.statusCode,
        ));
    }

    return processedResponse;
}

async function sendRequest(input: AlmeUIInput): Promise<AlmeUIMessage> {
    if (_settings.SmoothScroll.On) {
        var conversearea: any = document.getElementsByClassName(_settings.classNames.conversationAreaClassName);
        for (let i = 0; i < conversearea.length; i++) {
            let element = conversearea[i];
            if (input.messageType === _settings.messageConstants.getHistoryRequest) {
                element.style.scrollBehavior = "auto";
            }
            else {
                element.style.scrollBehavior = "smooth";
            }
        }

    }
    let selectedSettings = _settings;
    var accessibility = selectedSettings.accessibility || {};
    var labelledBy = accessibility.labelledBy || {};
	var elementName = labelledBy.root || "alme--window--header--banner";
    var roles = accessibility.role || {};
	var role = roles.rootElement || "dialog";
    let uidragclass = 'ui-draggable';
    let winelement = document.getElementsByClassName(`${_settings.classNames.windowClassName}`);
    let winelementwithdrag = document.getElementsByClassName(`${_settings.classNames.windowClassName} ${uidragclass}`);
    if(winelement.length > 0)
    {
        winelement[0].setAttribute('aria-modal', 'true');
	    winelement[0].setAttribute('aria-labelledby', elementName);
        winelement[0].setAttribute('role', role);
    }
    else if(winelementwithdrag.length > 0)
    {
        winelementwithdrag[0].setAttribute('aria-modal', 'true');
	    winelementwithdrag[0].setAttribute('aria-labelledby', elementName);
        winelementwithdrag[0].setAttribute('role', role);
    }
    const processedInput = handleInputProcessing(input);
    const accessKey = processedInput.accessKey ?? _settings.MultiTenant?.DefaultAccessKey;

    //map request
    const empty = (): [] => [];
    const requestType = processedInput.messageType;
    const result = {} as AlmeUIMessage;

    if (requestType === _settings.messageConstants.extensibleRequest){
        await sendExtensibleRequest(input, "POST");
        return result;
    }

    const isValid = validRequestTypes.includes(requestType);
    const requestFunc = isValid ? requests[requestType] : empty as AlmeUIMessage;
    try {
        result.response = await requestFunc(processedInput, accessKey);
        result.messageType = processedInput.messageType;
    }
    catch (err) {
        if(processedInput.messageType === _settings.messageConstants.converseRequest ||
            processedInput.messageType === _settings.messageConstants.requestUnitRequest ||
            processedInput.messageType === _settings.messageConstants.appeventRequest) {
                if (err.httpStatusCode){
                    result.statusCode = err.httpStatusCode;
                }
                result.errorText = generateErrorText(err.httpStatusCode);
                result.messageType = _settings.messageConstants.errorResponseType;
                result.response = generateErrorResponse();
                result.error = true;
        }
    }

    await handleResponseProcessing(result);

    return result;
}

export {
    setInputRequestProcessors,
    setResponseHandlers,
    setStore,
    setSettings,
    sendRequest,
    sendExtensibleRequest,
    getReducer,
    handleResponseProcessing,
};



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/lib-alme-request/src/lib-alme-request.ts